import { useCallback, useEffect, useState } from 'react'
import grayArrow from '../../assets/gray-arrow-icon.png'
import DesktopBanner from '../../components/DesktopBanner'
import FooterComplete from '../../components/FooterComplete'
import Header from '../../components/Header'
import MenuButton from '../../components/Header/MenuButton'
import Loader from '../../components/Loader'
import PointsExtractModal from '../../components/PointsExtractModal'
import Toolbar from '../../components/Toolbar'
import { useBottomModal } from '../../contexts/BottomModalContext'
import { RemoteLoadUserExpiration } from '../../data/use-case/RemoteLoadUserExpiration'
import { ApiHttpClient } from '../../infra/adapters/ApiHttpClient'
import api from '../../services/api'
import * as S from './style'

enum State {
    LOADING,
    READY
}

interface CatalogAttributes {
    catalogId: number
    name: string
    kpis: Array<{
        name: string
        value: number
    }>
    benefitBalance: number
    reinvestBalance: number
    expiration: any
}

interface ClientAttributes {
    id: string
    name: string
    catalogs: CatalogAttributes[]
}

interface WalletAttributes {
    currentState: State
    data: ClientAttributes[]
}

const httpClient = new ApiHttpClient()
const loadUserExpiration = new RemoteLoadUserExpiration(httpClient)

function Wallet () {
    const { showBottomModal } = useBottomModal()
    const [ClientShown, setClientShown] = useState<number | null>(null)
    const [state, setState] = useState<WalletAttributes>({
        currentState: State.LOADING,
        data: []
    })

    const toggleClient = useCallback((index: number) => {
        setClientShown(ClientShown === index ? null : index);
    }, [ClientShown])

    const sortClientsByName = useCallback((clients: ClientAttributes[]) => {
        return clients.sort((a: any, b: any) => {
            const nameA = a.name.toUpperCase()
            const nameB = b.name.toUpperCase()

            if (nameA < nameB) {
                return -1
            }
            if (nameA > nameB) {
                return 1
            }
            return 0
        })
    }, [])

    const maskWalletPoints = useCallback((points: number) => {
        const roundedValue = Math.floor(points * 100) / 100
        return roundedValue.toLocaleString('pt-br', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })
    }, [])

    const handleDownloadWallet = useCallback(() => {
        const header = ["Usuário", "Espécie", "Nome KPI", "Valor KPI", "Saldo Reinvestimento", "Total Benefícios"];
        const lines = [header.join(';')]

        state.data?.forEach(client => {
            client.catalogs.forEach(catalog => {
                catalog.kpis.forEach(kpi => {
                    const line = [
                        client.name,
                        catalog.name,
                        kpi.name.toString(),
                        kpi.value + '%',
                        maskWalletPoints(catalog.reinvestBalance),
                        maskWalletPoints(catalog.benefitBalance)
                    ];
                    lines.push(line.map(cell => `"${cell.replace(/"/g, '""')}"`).join(';'))
                })
            });
        });
        const csvContent = "\uFEFF" + lines.join('\r\n')

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' })
        const link = document.createElement("a")
        link.setAttribute("href", URL.createObjectURL(blob))
        link.setAttribute("download", "Carteira.csv")
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

    }, [maskWalletPoints, state.data])


    const calculateBenefitsBalance = useCallback((catalogs: CatalogAttributes[]) => {
        let sum = 0

        for (let i = 0; i < catalogs.length; i++) {
            sum += catalogs[i].benefitBalance
        }
        return maskWalletPoints(sum)
    }, [maskWalletPoints])


    useEffect(() => {
        (async () => {
            setState((state) => ({ ...state, currentState: State.LOADING }))
            const response = await api.get(`/wallet`)
            const promise = response.data.map(async (client: ClientAttributes) => {
                const promise = client.catalogs.map(async catalog => {
                    console.log('catalog', catalog.catalogId)
                    const expiration = await loadUserExpiration.handle({ userId: client.id, catalogId: catalog.catalogId })
                    if (!expiration.expiration) {
                        return catalog
                    }

                    return {
                        ...catalog,
                        expiration
                    }
                })
                const catalogs = await Promise.all(promise)
                return {
                    ...client,
                    catalogs
                }
            })
            const data = await Promise.all(promise)
            setState((state) => ({
                currentState: State.READY,
                data: sortClientsByName(data)
            }))
        })()
    }, [sortClientsByName])

    if (state.currentState === State.LOADING) return <Loader />

    return <S.WalletContent>
        <Header element={<MenuButton />} logo={false} pageTitle='Carteira' borderBottom showDesktopWidgets />
        <DesktopBanner title='Carteira' />
        <S.Wallet>
            <h2>
                Acompanhe todos os dados de sua carteira.
            </h2>
            <S.WalletInfos>
                <div>Você tem <span>{state.data.length}</span> {state.data.length === 1 ? 'cliente' : 'clientes'}.</div>
                <button onClick={handleDownloadWallet}>Baixar carteira</button>
            </S.WalletInfos>
        </S.Wallet>
        <S.WalletBottomSection>
            <h2>Confira a lista completa:</h2>
            <div>
                {state.data.map((client, index) => (
                    <S.WalletItem key={index}>
                        <S.WalletItemHeader onClick={() => toggleClient(index)} $isToggled={ClientShown === index}>
                            <p>{client.name}</p>
                            <img src={grayArrow} alt="Ícone de seta, indicando um link que pode ser clicado" />
                        </S.WalletItemHeader>
                        <S.WalletItemContent $isShown={ClientShown === index}>
                            {client.catalogs.map((catalog, index) => <S.WalletItemContentClient key={index} className=''>
                                <h3>Espécie {catalog.name}</h3>
                                {catalog.kpis.map(kpi => (
                                    <div>
                                        <p>{kpi.name}</p>
                                        <span>{kpi.value}%</span>
                                    </div>
                                ))}
                                <div>
                                    <p>Saldo Benefício</p>
                                    <span>{maskWalletPoints(catalog.benefitBalance)}</span>
                                </div>
                                <div>
                                    <p>Saldo Reinvestimento</p>
                                    <span>{maskWalletPoints(catalog.reinvestBalance)}</span>
                                </div>
                                {catalog.expiration && <div>
                                    <p>Pontos a expirar ({catalog.expiration.day})</p>
                                    <span>{maskWalletPoints(catalog.expiration.expiration.value)}</span>
                                </div>}
                            </S.WalletItemContentClient>)}
                            {client.catalogs.length > 1 && <>
                                <div className='line'></div>
                                <S.BenefitsBalance>
                                    <p>Total em Benefício</p>
                                    <span>{calculateBenefitsBalance(client.catalogs)}</span>
                                </S.BenefitsBalance>
                            </>}
                            <button onClick={() => { showBottomModal(<PointsExtractModal id={client.id} />, 'large') }}>Extrato de pontos</button>
                        </S.WalletItemContent>
                    </S.WalletItem>
                ))}
            </div>
            <S.WalletButton onClick={() => window.scrollTo(0, 0)}>
                Voltar ao topo
            </S.WalletButton>
        </S.WalletBottomSection>
        <S.FooterCompleteContainer>
            <FooterComplete />
        </S.FooterCompleteContainer>
        <Toolbar />
    </S.WalletContent>
}

export default Wallet